/**
 * The two inbound doors: uploading a pack file, and following a feed.
 *
 * Split from `imports.tsx`, which shows what has already arrived. This file is
 * how it arrives, and its job is mostly a reader waiting: a whole corpus is
 * several hundred files, every one checked and stored, so an upload is the one
 * act in the workspace slow enough to need a progress bar.
 *
 * Side by side because the difference is the point. A pack file is a snapshot
 * — taken once, and its author revising their copy never reaches the reader. A
 * feed is a live connection, and keeps arriving. The copy leans on that rather
 * than explaining it, and each door is its own panel with its own outcome, so a
 * reader cannot pick one expecting the other's behaviour or read one door's
 * failure as the other's.
 */

import { useState, type DragEvent, type JSX, type ReactNode } from 'react'

import { UserFacingError, describeFailure } from '@shared/user-errors.js'

import { Failure } from './cardDisplay'
import type { UploadProgress } from '../account'
import { subscribeToFeed, uploadPack } from '../account'
import type { Card } from '../cards'
import { t, type StringKey } from '../i18n'

const BYTES_PER_MEGABYTE = 1024 * 1024

export function BringFindingsIn({
  corpus,
  onChanged,
  onOpen,
}: {
  corpus: string | null
  onChanged: () => void
  onOpen: (card: Card) => void
}): JSX.Element {
  const [consent, setConsent] = useState(false)

  return (
    <section className="bring-in">
      <h3>{t('imports.bring')}</h3>

      {/* One consent for both doors, above both, because it is about storing
          study content at all rather than about which route it arrived by. */}
      <label className="bring-in__consent">
        <input
          type="checkbox"
          checked={consent}
          onChange={(event) => setConsent(event.target.checked)}
        />
        <span>{t('imports.consent')}</span>
      </label>

      <div className="bring-in__doors">
        <UploadDoor corpus={corpus} consent={consent} onChanged={onChanged} onOpen={onOpen} />
        <FollowDoor corpus={corpus} consent={consent} onChanged={onChanged} />
      </div>
    </section>
  )
}

/** What happened at one door: nothing yet, running, refused, or done. */
interface Outcome {
  busy: boolean
  failure: string | null
  done: string | null
}

/**
 * Ask first, then run, then say what happened — one job for both doors.
 *
 * Each door holds its own, so an upload's failure is shown at the upload and a
 * feed's at the feed. Consent is checked here as well as by the server, because
 * the server's refusal is a 403 after an upload has already been spooled and the
 * reader's answer is one checkbox away. The guard does not license a literal
 * `true` on the request afterwards: both doors send the `consent` the reader
 * gave, so the Article 9 agreement that reaches the server is theirs.
 */
function useAttempt(
  consent: boolean,
  whatFailed: StringKey,
  onChanged: () => void,
): Outcome & { attempt: (work: () => Promise<string>) => Promise<boolean> } {
  const [outcome, setOutcome] = useState<Outcome>({ busy: false, failure: null, done: null })

  async function attempt(work: () => Promise<string>): Promise<boolean> {
    if (!consent) {
      setOutcome({ busy: false, failure: t('imports.needsConsent'), done: null })
      return false
    }
    setOutcome({ busy: true, failure: null, done: null })
    try {
      const done = await work()
      setOutcome({ busy: false, failure: null, done })
      onChanged()
      return true
    } catch (error) {
      setOutcome({ busy: false, failure: describeFailure(error, t(whatFailed)), done: null })
      return false
    }
  }

  return { ...outcome, attempt }
}

/** A pack file: a snapshot, taken once. */
function UploadDoor({
  corpus,
  consent,
  onChanged,
  onOpen,
}: {
  corpus: string | null
  consent: boolean
  onChanged: () => void
  onOpen: (card: Card) => void
}): JSX.Element {
  const [file, setFile] = useState<File | null>(null)
  const [progress, setProgress] = useState<UploadProgress | null>(null)
  // Whose import just landed, for the "open them" button beside the result.
  const [importedAuthor, setImportedAuthor] = useState<string | null>(null)
  // A new key empties the native input after a success; its value is not settable.
  const [pickerKey, setPickerKey] = useState(0)
  const outcome = useAttempt(consent, 'imports.uploadFailed', onChanged)

  function choose(chosen: File | null) {
    setFile(chosen)
    setImportedAuthor(null)
  }

  async function upload() {
    setImportedAuthor(null)
    await outcome.attempt(async () => {
      if (file === null) throw new UserFacingError(t('imports.uploadNoFile'))
      try {
        const result = await uploadPack(file, corpus ?? '', consent, setProgress)
        setImportedAuthor(result.author)
        setFile(null)
        setPickerKey((key) => key + 1)
        return t('imports.uploaded', {
          count: result.findings,
          author: result.author_name || result.author,
        })
      } finally {
        setProgress(null)
      }
    })
  }

  return (
    <div className="door">
      <h4>{t('imports.uploadHeading')}</h4>
      <p className="muted">{t('imports.uploadNote')}</p>
      <PackPicker key={pickerKey} file={file} isBusy={outcome.busy} onChoose={choose} />
      <button type="button" className="door__go" disabled={outcome.busy} onClick={() => void upload()}>
        {outcome.busy ? t('imports.uploading') : t('imports.upload')}
      </button>
      <UploadOutcome
        progress={progress}
        outcome={outcome}
        importedAuthor={importedAuthor}
        onOpen={onOpen}
      />
    </div>
  )
}

/** Under the button, where the eye already is: how far it got, then how it went. */
function UploadOutcome({
  progress,
  outcome,
  importedAuthor,
  onOpen,
}: {
  progress: UploadProgress | null
  outcome: Outcome
  importedAuthor: string | null
  onOpen: (card: Card) => void
}): JSX.Element {
  return (
    <>
      {progress !== null && <ProgressLine progress={progress} />}
      {outcome.done !== null && (
        <DoneLine message={outcome.done}>
          {importedAuthor !== null && (
            <button
              type="button"
              className="link"
              onClick={() => onOpen({ kind: 'imported', author: importedAuthor })}
            >
              {t('imports.openImported')}
            </button>
          )}
        </DoneLine>
      )}
      {outcome.failure !== null && <Failure message={outcome.failure} />}
    </>
  )
}

/** What a door says once it worked, with room for the next step beside it. */
function DoneLine({ message, children }: { message: string; children?: ReactNode }): JSX.Element {
  return (
    <p className="door__done" role="status">
      {message}
      {children}
    </p>
  )
}

/**
 * The file picker, which is also a drop target.
 *
 * The native picker's own button speaks the browser's language and ignores the
 * page's styles, so the input is hidden and its label is the control: clicking
 * it opens the same picker, and a file can be dropped on it.
 */
function PackPicker({
  file,
  isBusy,
  onChoose,
}: {
  file: File | null
  isBusy: boolean
  onChoose: (file: File | null) => void
}): JSX.Element {
  const [isDragging, setIsDragging] = useState(false)

  function drop(event: DragEvent<HTMLLabelElement>) {
    event.preventDefault()
    setIsDragging(false)
    if (!isBusy) onChoose(event.dataTransfer.files[0] ?? null)
  }

  return (
    <label
      className={`dropzone${isDragging ? ' dropzone--over' : ''}${isBusy ? ' dropzone--busy' : ''}`}
      onDragOver={(event) => {
        event.preventDefault()
        setIsDragging(true)
      }}
      onDragLeave={() => setIsDragging(false)}
      onDrop={drop}
    >
      <input
        className="dropzone__input"
        type="file"
        accept=".zip,application/zip"
        disabled={isBusy}
        onChange={(event) => onChoose(event.target.files?.[0] ?? null)}
      />
      {file === null ? (
        <>
          <span className="dropzone__action">{t('imports.uploadChoose')}</span>
          <span className="muted">{t('imports.uploadDrop')}</span>
        </>
      ) : (
        <>
          <span className="dropzone__file">{file.name}</span>
          <span className="muted">
            {t('imports.megabytes', { size: (file.size / BYTES_PER_MEGABYTE).toFixed(1) })}
            {' · '}
            {t('imports.uploadChange')}
          </span>
        </>
      )}
    </label>
  )
}

/**
 * The bar and the sentence under it.
 *
 * A percentage while bytes travel; a moving segment once they have all
 * arrived, because the server does not report how far through the findings it
 * is, and a bar parked at 100% while the import is still running reads as a
 * hang.
 */
function ProgressLine({ progress }: { progress: UploadProgress }): JSX.Element {
  const sentence =
    progress.phase === 'sending'
      ? t('imports.uploadSending', { percent: Math.round(progress.fraction * 100) })
      : t('imports.uploadImporting')
  return (
    <div className="door__progress" role="status" aria-live="polite">
      {progress.phase === 'sending' ? (
        <progress max={1} value={progress.fraction} aria-label={sentence} />
      ) : (
        <div className="door__working" role="progressbar" aria-label={sentence} />
      )}
      <span className="muted">{sentence}</span>
    </div>
  )
}

/** A feed on this site: a live connection, and it keeps arriving. */
function FollowDoor({
  corpus,
  consent,
  onChanged,
}: {
  corpus: string | null
  consent: boolean
  onChanged: () => void
}): JSX.Element {
  const [feed, setFeed] = useState('')
  const [pack, setPack] = useState('')
  const outcome = useAttempt(consent, 'imports.followFailed', onChanged)

  const follow = () =>
    outcome.attempt(async () => {
      if (feed.trim() === '' || pack.trim() === '') throw new UserFacingError(t('imports.followIncomplete'))
      const added = await subscribeToFeed(feed, pack, corpus ?? '', consent)
      setFeed('')
      setPack('')
      return t('imports.followed', {
        author: added.author_name || added.author,
        pack: added.title,
      })
    })

  return (
    <div className="door">
      <h4>{t('imports.followHeading')}</h4>
      <p className="muted">{t('imports.followNote')}</p>
      <div className="form">
        <label>
          {t('imports.followUrl')}
          <input
            type="url"
            value={feed}
            placeholder={t('imports.followUrlExample')}
            onChange={(event) => setFeed(event.target.value)}
          />
        </label>
        <label>
          {t('imports.followPack')}
          <input type="text" value={pack} onChange={(event) => setPack(event.target.value)} />
        </label>
      </div>
      <button
        type="button"
        className="door__go"
        disabled={outcome.busy}
        onClick={() => void follow()}
      >
        {t('imports.follow')}
      </button>
      {outcome.done !== null && <DoneLine message={outcome.done} />}
      {outcome.failure !== null && <Failure message={outcome.failure} />}
    </div>
  )
}
